import { AssetGroup, useGrouping } from '#base/AssetGroup.js';
import { useCancel } from '#base/Canvas/Cancel.js';
import { ColorPicker } from '#base/Color.js';
import { FontToolbar, JerseyToolbar, MonogramToolbar } from '#base/Designer/Toolbars/index.js';
import { ToolKeys, ToolViewModes } from '#base/Designer/types.js';
import Button from '#base/ui/Button.js';
import { DEFAULT_COLOR } from '@c2s-npm/plumlib';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

export const ToolView = ({
  toolKey,
  align,
  setAlign,
  assets: allAssets,
  groups,
  onSelect,
  selected,
  cancel,
  revert,
  elements,
  hasPremium,
  renderInput,
  renderAssets,
  placeholder,
  debounce = 100,
  graphicType,
  defaultColor,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [mode, setMode] = useState(ToolViewModes.INPUT);
  const [selectedColor, setColor] = useState(selected?.get?.('color') || defaultColor || DEFAULT_COLOR);
  const [selectedAsset, setAsset] = useState(
    allAssets.find((f) => f.key === selected?.get?.('assetId')) || null
  );
  const [currentAsset, setCurrentAsset] = useState(selectedAsset);
  const [currentColor, setCurrentColor] = useState(selectedAsset);
  const [input, setInput] = useState({});

  const [commit, undo, canRevert, isNew] = useCancel(selected, cancel, revert);

  const [subGroups, assets, selectedGroup, selectGroup, hasPremiumElement, hasOtherElements] = useGrouping({
    groups,
    assets: allAssets,
    autoSelectGroup: selectedAsset?.key,
    elements,
    assetGroupFilter: [],
  });

  const showPremiumWarning = (hasPremium === 'item' || hasOtherElements) && hasPremiumElement;

  useEffect(() => {
    if (!selected && assets.length > 0) {
      setAsset(assets[0]);
    }
  }, [selected, assets]);

  useEffect(() => {
    setCurrentAsset(selectedAsset);
  }, [selectedAsset]);

  useEffect(() => {
    setCurrentColor(selectedColor);
  }, [selectedColor]);

  useEffect(() => {
    // TODO make this snappier for color/asset changes
    const timeoutId = setTimeout(() => {
      currentAsset && onSelect(currentAsset, Object.assign({}, input, { color: currentColor }));
    }, debounce);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentAsset, currentColor, input, debounce]);

  const onCancelClick = () => {
    if (mode === ToolViewModes.INPUT) undo();
    else {
      onSelect(selectedAsset, input);
      setCurrentAsset(selectedAsset);
      setCurrentColor(selectedColor);
      setMode(ToolViewModes.INPUT);
    }
  };

  const onAssetConfirmUpdate = () => {
    setAsset(currentAsset);
    setColor(currentColor);
    setMode(ToolViewModes.INPUT);
  };

  const shouldShowStep = (stepMode) => (!!toolKey && mode === stepMode) || !toolKey;

  return (
    <div className='t-mx-auto t-max-w-[360px]'>
      <div className='t-mb-4 t-block t-rounded-t-xl lg:t-relative lg:-t-left-10 lg:-t-top-8 lg:t-z-10 lg:t-mx-auto lg:t-mb-0 lg:t-w-[calc(100%+80px)] lg:t-border-b lg:t-border-neutrals-light-grey lg:t-bg-white lg:t-px-10 lg:t-py-3 lg:t-shadow-[0px_6px_6px_#00000019]'>
        {toolKey === ToolKeys.TEXT && <FontToolbar {...{ mode, setMode, graphicType, align, setAlign }} />}
        {toolKey === ToolKeys.MONOGRAM && (
          <MonogramToolbar {...{ mode, setMode, graphicType, selectedAsset: currentAsset }} />
        )}
        {toolKey === ToolKeys.JERSEY && <JerseyToolbar {...{ mode, setMode, graphicType }} />}
      </div>
      <AssetGroup groups={subGroups} {...{ selectedGroup, selectGroup }} />
      {subGroups.length === 0 && assets.length === 0 && (
        <em className='t-font-bold t-not-italic t-text-muted'>Empty group</em>
      )}
      {assets.length > 0 && (
        <>
          <div
            className={classNames({
              't-hidden': !shouldShowStep(ToolViewModes.INPUT),
              't-block': shouldShowStep(ToolViewModes.INPUT),
            })}>
            {renderInput({
              selected,
              input,
              setInput,
              align,
              commit,
              undo,
              canRevert,
              isNew,
              placeholder,
              graphicType,
              defaultColor,
            })}
          </div>

          <div
            className={classNames({
              't-hidden': !shouldShowStep(ToolViewModes.ASSET) && !shouldShowStep(ToolViewModes.ASSET_FRAMES),
              't-block': shouldShowStep(ToolViewModes.ASSET) || shouldShowStep(ToolViewModes.ASSET_FRAMES),
            })}>
            {renderAssets({
              selected,
              input,
              setInput,
              assets,
              selectedAsset: currentAsset,
              setAsset: setCurrentAsset,
              hasPremium: hasOtherElements || hasPremium === 'item',
              placeholder,
              graphicType,
              defaultColor,
              mode: toolKey ? mode : undefined,
            })}
            {showPremiumWarning && (
              <div className='t-text-left t-text-brand-red'>
                Premium assets cannot be used alongside other designs and text.
              </div>
            )}
          </div>

          <div
            className={classNames({
              't-hidden': !shouldShowStep(ToolViewModes.COLOR),
              't-block': shouldShowStep(ToolViewModes.COLOR),
            })}>
            <ColorPicker value={currentColor} onSelect={setCurrentColor} />
          </div>
        </>
      )}

      {(!assets.length || mode !== ToolViewModes.INPUT) && (
        <div className='t-relative t-z-30 t-mt-5 t-w-auto t-text-center '>
          <div className='t-flex t-items-center t-justify-center t-gap-3 t-text-center lg:t-flex-col'>
            <Button
              size={isMobile ? 'sm' : 'base'}
              variant='primary'
              fullWidth='all'
              onClick={onAssetConfirmUpdate}>
              {getButtonLabel(mode, toolKey)}
            </Button>
            <Button size={isMobile ? 'sm' : 'base'} variant='link' onClick={onCancelClick}>
              {mode === ToolViewModes.INPUT ? 'Cancel' : 'Back'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const getButtonLabel = (mode, toolKey) => {
  if (mode === ToolViewModes.COLOR) return 'Update Color';

  if (toolKey === ToolKeys.TEXT) {
    if (mode === ToolViewModes.ASSET) return 'Update Font';
  }

  if ([ToolKeys.MONOGRAM, ToolKeys.JERSEY].includes(toolKey)) {
    if (mode === ToolViewModes.ASSET) return 'Update Style';
    if (mode === ToolViewModes.ASSET_FRAMES) return 'Update Frame'; // applies on monogram only
  }

  return 'Update';
};
